/** Shared, dependency-free world contract for canvas rendering and authoring. */
export const CANVAS_CELL_SIZE = 60;
export const CANVAS_BLOCK_SIZE = CANVAS_CELL_SIZE * 3;
export const CANVAS_SECTOR_SIZE = CANVAS_CELL_SIZE * 9;
export const CANVAS_WORLD_SECTOR_COUNT = 64;
export const CANVAS_WORLD_SIZE = CANVAS_SECTOR_SIZE * CANVAS_WORLD_SECTOR_COUNT;
export const CANVAS_WORLD_MIN = -CANVAS_WORLD_SIZE / 2;
export const CANVAS_WORLD_MAX = CANVAS_WORLD_SIZE / 2;
export const CANVAS_MIN_ZOOM = 0.02;
/** Authored canvases are bounded. A read-only projection (Flow) owns no
 * coordinates of its own and draws a graph of any size: it is unbounded. */
export type CanvasWorld = 'bounded' | 'unbounded';
const GEOMETRY_EPSILON = 1e-7;

export interface CanvasWorldRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export const CANVAS_WORLD_BOUNDS: CanvasWorldRect = Object.freeze({
  x: CANVAS_WORLD_MIN,
  y: CANVAS_WORLD_MIN,
  w: CANVAS_WORLD_SIZE,
  h: CANVAS_WORLD_SIZE,
});

export function isWithinCanvasWorld(rect: CanvasWorldRect): boolean {
  return (
    [rect.x, rect.y, rect.w, rect.h].every(Number.isFinite) &&
    rect.w >= 0 &&
    rect.h >= 0 &&
    rect.x >= CANVAS_WORLD_MIN - GEOMETRY_EPSILON &&
    rect.y >= CANVAS_WORLD_MIN - GEOMETRY_EPSILON &&
    rect.x + rect.w <= CANVAS_WORLD_MAX + GEOMETRY_EPSILON &&
    rect.y + rect.h <= CANVAS_WORLD_MAX + GEOMETRY_EPSILON
  );
}

/** A write preflight, never a read-time repair or document migration. */
export function assertCanvasWorldRect(rect: CanvasWorldRect): void {
  if (!isWithinCanvasWorld(rect)) {
    throw new RangeError(
      'Canvas bounds exceeded: keep the complete object inside ' +
        `x/y ${CANVAS_WORLD_MIN}…${CANVAS_WORLD_MAX}. Reduce its size or choose another position.`
    );
  }
}

function readCanvasWorldRect(value: unknown): CanvasWorldRect {
  let tuple: unknown = value;
  if (typeof value === 'string') {
    try {
      tuple = JSON.parse(value);
    } catch {
      throw new TypeError('Invalid canvas geometry');
    }
  }
  if (
    !Array.isArray(tuple) ||
    tuple.length !== 4 ||
    !tuple.every(Number.isFinite)
  ) {
    throw new TypeError('Invalid canvas geometry');
  }
  return { x: tuple[0], y: tuple[1], w: tuple[2], h: tuple[3] };
}

export function assertCanvasWorldXYWH(
  value: unknown,
  previous?: unknown
): void {
  const rect = readCanvasWorldRect(value);
  if (isWithinCanvasWorld(rect)) return;
  // Older outliers may stay put or move inward, never expand their overflow.
  // This is a write allowance, not a migration or a mutation during hydration.
  if (previous !== undefined && rect.w >= 0 && rect.h >= 0) {
    const old = readCanvasWorldRect(previous);
    if (
      rect.x >= Math.min(CANVAS_WORLD_MIN, old.x) &&
      rect.y >= Math.min(CANVAS_WORLD_MIN, old.y) &&
      rect.x + rect.w <= Math.max(CANVAS_WORLD_MAX, old.x + old.w) &&
      rect.y + rect.h <= Math.max(CANVAS_WORLD_MAX, old.y + old.h)
    )
      return;
  }
  assertCanvasWorldRect(rect);
}

export function placeCanvasWorldXYWH(value: unknown): string {
  const rect = placeInsideCanvasWorld(readCanvasWorldRect(value));
  return JSON.stringify([rect.x, rect.y, rect.w, rect.h]);
}

/** Authoring coordinates only; hit-testing and the camera remain unmodified. */
export function constrainCanvasWorldPoint(
  [x, y]: readonly number[],
  inset = 0
): [number, number] {
  const min = CANVAS_WORLD_MIN + inset,
    max = CANVAS_WORLD_MAX - inset;
  return [Math.max(min, Math.min(max, x)), Math.max(min, Math.min(max, y))];
}

/** Translate an insertion as a whole; never shrink it or rearrange its children. */
export function placeInsideCanvasWorld<T extends CanvasWorldRect>(rect: T): T {
  if (
    ![rect.x, rect.y, rect.w, rect.h].every(Number.isFinite) ||
    rect.w < 0 ||
    rect.h < 0 ||
    rect.w > CANVAS_WORLD_SIZE ||
    rect.h > CANVAS_WORLD_SIZE
  ) {
    throw new RangeError(
      'Object is too large for the canvas. Reduce its size before inserting.'
    );
  }
  return {
    ...rect,
    x: Math.max(CANVAS_WORLD_MIN, Math.min(CANVAS_WORLD_MAX - rect.w, rect.x)),
    y: Math.max(CANVAS_WORLD_MIN, Math.min(CANVAS_WORLD_MAX - rect.h, rect.y)),
  };
}

/** One shared delta prevents a multi-selection from collapsing at the edge. */
export function constrainCanvasWorldDelta(
  rects: readonly CanvasWorldRect[],
  dx: number,
  dy: number
) {
  let minX = -Infinity,
    maxX = Infinity,
    minY = -Infinity,
    maxY = Infinity;
  for (const rect of rects) {
    // Existing outlying content may move inward without jumping on first drag.
    minX = Math.max(minX, Math.min(0, CANVAS_WORLD_MIN - rect.x));
    maxX = Math.min(maxX, Math.max(0, CANVAS_WORLD_MAX - rect.x - rect.w));
    minY = Math.max(minY, Math.min(0, CANVAS_WORLD_MIN - rect.y));
    maxY = Math.min(maxY, Math.max(0, CANVAS_WORLD_MAX - rect.y - rect.h));
  }
  return {
    dx: Math.max(minX, Math.min(maxX, dx)),
    dy: Math.max(minY, Math.min(maxY, dy)),
  };
}

/** Limit an entire resize to one interpolation factor, preserving its anchor. */
export function canvasWorldResizeProgress(
  pairs: readonly {
    original: CanvasWorldRect;
    next: CanvasWorldRect;
  }[]
): number {
  let progress = 1;
  for (const { original, next } of pairs) {
    const from = [
      original.x,
      original.y,
      original.x + original.w,
      original.y + original.h,
    ];
    const to = [next.x, next.y, next.x + next.w, next.y + next.h];
    for (let i = 0; i < 4; i++) {
      const limit =
        i < 2
          ? Math.min(CANVAS_WORLD_MIN, from[i])
          : Math.max(CANVAS_WORLD_MAX, from[i]);
      if ((i < 2 && to[i] < limit) || (i >= 2 && to[i] > limit)) {
        progress = Math.min(progress, (limit - from[i]) / (to[i] - from[i]));
      }
    }
  }
  return Math.max(0, progress);
}

/** Start view of an empty canvas: the upper left quadrant (x < 0, y < 0) fills
 * the view, the origin lies one cell beyond its lower right corner. */
export function initialCanvasWorldView(width: number, height: number) {
  const reference = Math.hypot(1512, 982);
  const diagonal = Math.hypot(width, height);
  const zoom =
    diagonal > 0
      ? Math.min(
          0.65,
          Math.max(0.35, 0.5 * Math.pow(reference / diagonal, 0.4))
        )
      : 0.5;
  return {
    zoom,
    centerX: -(width / 2 + CANVAS_CELL_SIZE) / zoom,
    centerY: -(height / 2 + CANVAS_CELL_SIZE) / zoom,
  };
}
